// document.body carries mode-land, mode-menu, mode-controls and mode-editor
// as four independent flags today, not one exclusive value: none of
// showMenu/hideMenu, the key list's toggle, enterLand/returnToShip or
// editor/shell.ts's toggleMode ever clears another one's class, src/input.ts
// lets the L key reach enterLand without checking isMenuOpen, and the
// backtick handler in src/main.ts reaches toggleEditorMode without checking
// isLandMode or isMenuMode. Boot itself proves it: `?editor=1` with no
// `?scenario=` runs showMenu() (adds mode-menu) and toggleEditorMode() (adds
// mode-editor) back to back. Flattening these into one value would drop
// whichever class was already on the body the moment a second one is set,
// so setMode takes an explicit on/off per flag instead of assuming one
// flag's class replaces another's.
//
// 'cruise' and 'fight' are not classList states at all: fight is
// `engagement !== null` in src/main.ts, and this step does not wire either
// one. getMode() is a priority-ordered single label over the active flags
// for a later slice's use, not read by any site converted in this step.

// 'dvtable' is the delta-v map's wide window (src/hudDvTable.ts): open, it takes
// every key and keeps the wheel and the mouse-look off the camera.
//
// 'modal' is the same idea for a true modal layered over everything else -
// the building detail lightbox (src/buildingDetail.ts) is the first one.
// Unlike 'menu' (a HUD panel a player still flies and warps through) or
// 'dvtable' (its own bespoke capture listener), 'modal' is a plain shared
// flag two OTHER modules read: src/input.ts bails out of the warp digits
// while it is set, and src/decisionPanel.ts's own capture listener bails out
// of a pick the same way, rather than each modal inventing its own private
// coordination with everything that could still be listening underneath it.
export type AppMode = 'cruise' | 'fight' | 'menu' | 'controls' | 'land' | 'editor' | 'dvtable' | 'modal';

/** Whether a true modal (src/buildingDetail.ts today) is on top of
 *  everything else right now - see the 'modal' note above `AppMode`. */
export function isModalOpen(): boolean {
  return isMode('modal');
}

export function setModalOpen(on: boolean): void {
  setMode('modal', on);
}

type Listener = (mode: AppMode, on: boolean) => void;

const active = new Set<AppMode>();
const listeners = new Set<Listener>();

/** Plain Set lookup: safe to call from a per-frame hot path, no DOM read. */
export function isMode(mode: AppMode): boolean {
  return active.has(mode);
}

export function setMode(mode: AppMode, on: boolean): void {
  const was = active.has(mode);
  if (was === on) return;
  if (on) active.add(mode);
  else active.delete(mode);
  for (const fn of listeners) fn(mode, on);
}

export function onModeChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const MODE_PRIORITY: AppMode[] = ['land', 'editor', 'menu', 'controls', 'fight'];

/** The single highest-priority active flag, cruise when none are set. */
export function getMode(): AppMode {
  for (const m of MODE_PRIORITY) {
    if (active.has(m)) return m;
  }
  return 'cruise';
}

const MODE_CLASS: Partial<Record<AppMode, string>> = {
  menu: 'mode-menu', controls: 'mode-controls', land: 'mode-land', editor: 'mode-editor',
  dvtable: 'mode-dvtable',
};

/**
 * Mirror the typed mode flags onto body classes so every stylesheet selector
 * (body.mode-land, body.mode-menu, ...) keeps working unchanged. Both play and
 * explore boots call this before initMenu()/showMenu(), so the boot sequence's
 * own first writes go through it, not around it.
 */
export function mirrorModeClasses(): void {
  onModeChange((mode, on) => {
    const cls = MODE_CLASS[mode];
    if (cls) document.body.classList.toggle(cls, on);
  });
}
